// UserManagement.tsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { adminService } from "../../services/adminService";
import { User } from "../../types";

const UserManagement: React.FC = () => {
    const { user } = useAuth();
    const [users, setUsers] = useState<User[]>([]);
    const [pending, setPending] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadUsers();
        loadPending();
    }, []);

    const loadUsers = async () => {
        setLoading(true);
        try {
            const response = await adminService.getAllUsers?.();
            if (response && response.users) setUsers(response.users);
        } catch (e) {
            // fallback: ignore if endpoint not implemented
        } finally {
            setLoading(false);
        }
    };

    const loadPending = async () => {
        try {
            const response = await adminService.getPendingRegistrations();
            setPending(response.users || []);
        } catch (e) {
            setPending([]);
        }
    };

    const handleApprove = async (userId: number) => {
        await adminService.approveRegistration(userId);
        loadPending();
        loadUsers();
    };

    const handleReject = async (userId: number) => {
        await adminService.rejectRegistration(userId);
        loadPending();
        loadUsers();
    };

    if (user?.role !== "admin") {
        return (
            <div className="min-h-screen bg-eldai-gray-light flex items-center justify-center">
                <div className="bg-white p-8 rounded-lg shadow-md text-center">
                    <h1 className="text-2xl font-bold text-red-600 mb-4">Accès refusé</h1>
                    <p className="text-eldai-gray">
                        Vous n'avez pas les permissions pour accéder à cette page.
                    </p>
                    <Link to="/" className="btn btn-primary mt-4 inline-block">
                        Retour à l'accueil
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-eldai-gray-light">
            <div className="container mx-auto px-4 py-8">
                <div className="mb-8">
                    <div className="flex items-center mb-4">
                        <Link
                            to="/admin"
                            className="text-eldai-blue hover:text-eldai-light-blue mr-4"
                        >
                            ← Retour au tableau de bord
                        </Link>
                    </div>
                    <h1 className="text-3xl font-bold text-eldai-blue mb-2">
                        Gestion des utilisateurs
                    </h1>
                    <p className="text-eldai-gray">
                        Gérez les comptes clients et validez les inscriptions.
                    </p>
                </div>

                {/* Pending Registrations */}
                <div className="mb-8">
                    <h2 className="text-xl font-bold text-yellow-700 mb-4">
                        Inscriptions en attente
                    </h2>
                    {pending.length === 0 ? (
                        <p className="text-eldai-gray">Aucune inscription en attente.</p>
                    ) : (
                        <table className="w-full bg-white rounded-lg shadow-md mb-4">
                            <thead>
                                <tr>
                                    <th className="px-4 py-2 text-left">Nom</th>
                                    <th className="px-4 py-2 text-left">Email</th>
                                    <th className="px-4 py-2 text-left">Type</th>
                                    <th className="px-4 py-2 text-left">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pending.map((u) => (
                                    <tr key={u.id}>
                                        <td className="px-4 py-2">{u.first_name} {u.last_name}</td>
                                        <td className="px-4 py-2">{u.email}</td>
                                        <td className="px-4 py-2">{u.account_type}</td>
                                        <td className="px-4 py-2">
                                            <button
                                                className="bg-green-600 text-white px-3 py-1 rounded mr-2"
                                                onClick={() => handleApprove(u.id)}
                                            >
                                                Valider
                                            </button>
                                            <button
                                                className="bg-red-600 text-white px-3 py-1 rounded"
                                                onClick={() => handleReject(u.id)}
                                            >
                                                Rejeter
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* All Users */}
                <div>
                    <h2 className="text-xl font-bold text-eldai-blue mb-4">
                        Tous les utilisateurs
                    </h2>
                    {loading ? (
                        <p>Chargement...</p>
                    ) : users.length === 0 ? (
                        <p className="text-eldai-gray">Aucun utilisateur trouvé.</p>
                    ) : (
                        <table className="w-full bg-white rounded-lg shadow-md">
                            <thead>
                                <tr>
                                    <th className="px-4 py-2 text-left">Nom</th>
                                    <th className="px-4 py-2 text-left">Email</th>
                                    <th className="px-4 py-2 text-left">Type</th>
                                    <th className="px-4 py-2 text-left">Statut</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map((u) => (
                                    <tr key={u.id}>
                                        <td className="px-4 py-2">{u.first_name} {u.last_name}</td>
                                        <td className="px-4 py-2">{u.email}</td>
                                        <td className="px-4 py-2">{u.account_type}</td>
                                        <td className="px-4 py-2">
                                            {u.approval_status === "approved"
                                                ? "✅ Approuvé"
                                                : u.approval_status === "pending"
                                                    ? "⏳ En attente"
                                                    : u.approval_status === "rejected"
                                                        ? "❌ Rejeté"
                                                        : ""}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
};

export default UserManagement;
